import { ParsedSchedule } from "./parseScheduleExcel";
import { ImportShiftRow } from "../network/scheduleUploadApi";

/** Adds 8 hours to a "HH:MM:SS" string, returns "HH:MM:SS". */
function addEightHours(time: string): string {
  const [h, m, s] = time.split(":").map(Number);
  const totalMinutes = h * 60 + m + 8 * 60;
  const endH = Math.floor(totalMinutes / 60) % 24;
  const endM = totalMinutes % 60;
  return `${String(endH).padStart(2, "0")}:${String(endM).padStart(2, "0")}:${String(s ?? 0).padStart(2, "0")}`;
}

/**
 * Extracts a start-time from common schedule cell formats:
 * - "07:00"
 * - "07:00:00"
 * - "07:00-15:00" (uses the first time)
 * - "7:00 AM"
 */
function parseStartTime(value: string): string | null {
  const match = value.trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([AaPp][Mm])?/);
  if (!match) return null;

  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const seconds = parseInt(match[3] ?? "0", 10);
  const ampm = match[4]?.toUpperCase();

  if (minutes > 59 || seconds > 59) return null;

  if (ampm) {
    if (hours < 1 || hours > 12) return null;
    if (ampm === "AM" && hours === 12) hours = 0;
    if (ampm === "PM" && hours !== 12) hours += 12;
  } else if (hours > 23) {
    return null;
  }

  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

/**
 * Converts parsed Excel schedule data into a flat array of shift rows ready
 * for the backend import endpoint.
 *
 * Rules:
 * - Rows with no employee_id (unmatched) are skipped.
 * - Cells with value "-" or "Off" are skipped.
 * - "Leave" → record_type "T" (time-off), no start/end times.
 * - Time values like "07:00" → record_type "A", start = HH:MM:00, end = start + 8 h.
 *
 * Date conversion: "DD/MM" + current year → "YYYY-MM-DD".
 * Year rollover: if the month decreases between consecutive dates (e.g. Dec → Jan),
 * the year is incremented for all subsequent dates.
 */
export function prepareScheduleImportPayload(parsedData: ParsedSchedule): ImportShiftRow[] {
  const rows: ImportShiftRow[] = [];

  console.debug("[preparePayload] Total rows:", parsedData.rows.length);
  console.debug("[preparePayload] Rows with employee_id:", parsedData.rows.map(r => ({
    employee: r.employee,
    employee_id: r.employee_id,
  })));

  for (const empRow of parsedData.rows) {
    if (!empRow.employee_id) {
      console.debug("[preparePayload] Skipping (no employee_id):", empRow.employee);
      continue;
    }

    for (const week of empRow.weeks) {
      let year = new Date().getFullYear();
      let prevMonth = -1;

      for (const day of week.days) {
        const value = day.value.trim();
        if (!value || value === "-" || /^off\b/i.test(value)) continue;

        // Parse DD/MM
        const [dd, mm] = day.date.split("/").map(Number);
        if (!dd || !mm) continue;

        // Detect year rollover (e.g. schedule crosses Dec → Jan)
        if (prevMonth > 0 && mm < prevMonth) {
          year += 1;
        }
        prevMonth = mm;

        const fullDate = `${year}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;

        if (/^leave\b/i.test(value)) {
          rows.push({
            employee_id: empRow.employee_id,
            date: fullDate,
            start_time: null,
            end_time: null,
            record_type: "T",
          });
        } else {
          const startTime = parseStartTime(value);
          if (!startTime) continue;
          const endTime = addEightHours(startTime);

          rows.push({
            employee_id: empRow.employee_id,
            date: fullDate,
            start_time: startTime,
            end_time: endTime,
            record_type: "A",
          });
        }
      }
    }
  }

  return rows;
}
